import i18n from "@/i18n";

const { t } = i18n.global;

const CHART_SCRIPT_URL = "https://cdn.trychart.com/script.js";
const VOUCHED_SCRIPT_URL = "https://static.vouched.id/plugin/releases/latest/index.js";

export function getObjectKeys<TObject extends Record<string, unknown>>(obj: TObject) {
  return Object.keys(obj) as (keyof TObject)[];
}

export function hasUrlMatchingPath(url: string, paths: string[]) {
  const pathsRegex = paths.map((path) => {
    const regexString = path.replace(/:[^/]+/g, "([^/]+)");
    return new RegExp(`^${regexString}$`);
  });
  return pathsRegex.some((regex) => regex.test(url));
}

export const getColorFromCssVar = (name: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name);
};

export const makeRandomString = (length = 10): string => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getExtensionFromName = (originalName: string): string =>
  originalName.split(".").pop() ?? "";

export const areFilesExtensionsValid = (files: File[], extensions: string[]): boolean =>
  !files.some((file) => !extensions.includes(getExtensionFromName(file.name)));

export const prependHttps = (originalString: string) => {
  return originalString.replace(/^(?!https?:\/\/)/, "https://");
};

export const isTextSelectedWithin = (element: HTMLElement | null | undefined) => {
  const selection = window.getSelection();
  const anchorNode = selection?.anchorNode;

  return Boolean(selection?.toString() && anchorNode && element?.contains(anchorNode));
};

export const intersection = <T>(...arrays: T[][]): T[] => {
  if (arrays.length === 0) return [];
  const sets = arrays.map((arr) => new Set(arr));
  return [...sets[0]].filter((item) => sets.every((s) => s.has(item)));
};

export const loadThirdPartyServiceScript = (url: string, name: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(t("common.failedToLoadScript", { name })));
    document.head.appendChild(script);
  });
};

export const loadChartScript = () => {
  return loadThirdPartyServiceScript(CHART_SCRIPT_URL, "Chart");
};

export const loadVouchedScript = () => {
  return loadThirdPartyServiceScript(VOUCHED_SCRIPT_URL, "Vouched");
};
export const sleep = (timeMs: number) => new Promise((resolve) => setTimeout(resolve, timeMs));
